import React, { useCallback, useEffect, useState } from 'react'
import { downloadStudentApplicationDocument } from 'api/applicationDocuments'
import {
	syncStudentDocumentsSeen,
	syncStudentRecommendationsSeen,
} from 'shared/utils/counselorSeenSync'
import { downloadStudentLorFile } from 'api/lorRequests'
import PdfPreviewPortal from 'pages/ApplicationDetailsPage/components/Checklist/components/PdfPreviewPortal/PdfPreviewPortal'
import { ReactComponent as DockIcon } from 'assets/icons/dock.svg'
import { ReactComponent as EyeIcon } from 'assets/icons/openedEye.svg'
import { ReactComponent as ReloadIcon } from 'assets/icons/reload-2.svg'
import PriorityDrawerShell from '../PriorityDrawerShell/PriorityDrawerShell'
import s from './PriorityFilesDrawer.module.scss'

const VARIANT_CONFIG = {
	documents: {
		listKey: 'newDocuments',
		eyebrow: 'New from student',
		nounSingular: 'document',
		nounPlural: 'documents',
		markSeen: syncStudentDocumentsSeen,
		download: async (studentId, file) => {
			if (file?.kind === 'bragsheet') {
				return downloadStudentLorFile(studentId, file.id)
			}
			return downloadStudentApplicationDocument(studentId, file.id)
		},
		fallbackTitle: 'Document',
		resolveTitle: file => {
			if (file?.kind === 'bragsheet') return file.title || 'Bragsheet'
			return file.title || 'Document'
		},
	},
	recommendations: {
		listKey: 'uploadedFiles',
		eyebrow: 'Recommendations',
		nounSingular: 'file',
		nounPlural: 'files',
		markSeen: (studentId, opts) =>
			syncStudentRecommendationsSeen(studentId, {
				...opts,
				applicationTargetId: opts?.applicationTargetId,
				cardId: opts?.cardId,
			}),
		download: async (studentId, file) =>
			downloadStudentLorFile(studentId, file?.id || file),
		fallbackTitle: 'File',
		resolveTitle: file => file?.title || 'LoR',
	},
}

const formatUpdated = value => {
	if (!value) return ''
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return ''
	return date.toLocaleString('en-US', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	})
}

const FileRow = ({ file, onSee, onReload }) => {
	return (
		<div className={s.row}>
			<div className={s.row__main}>
				<span className={s.row__icon}>
					<DockIcon />
				</span>
				<div className={s.row__inner}>
					<span className={s.row__title}>{file.title}</span>
					<span className={s.row__meta}>
						{file.updatedLabel
							? `Updated: ${file.updatedLabel}`
							: 'Updated: —'}
					</span>
				</div>
			</div>
			<div className={s.row__actions}>
				<button
					type="button"
					className={s.iconBtn}
					onClick={() => onReload?.(file)}
					aria-label="Re-upload"
				>
					<span className={s.iconBtn__icon}>
						<ReloadIcon />
					</span>
					<span className={s.iconBtn__hint}>Re-upload</span>
				</button>
				<button
					type="button"
					className={s.actionBtn}
					onClick={() => onSee?.(file)}
				>
					<span className={s.actionBtn__icon}>
						<EyeIcon />
					</span>
					<span className={s.actionBtn__text}>See Document</span>
				</button>
			</div>
		</div>
	)
}

const PriorityFilesDrawer = ({
	variant = 'documents',
	items = [],
	index = 0,
	onClose,
	onNavigate,
	onChanged,
}) => {
	const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.documents
	const item = items[index]
	const [pdfPreview, setPdfPreview] = useState(null)

	useEffect(() => {
		if (!item?.studentId) return
		const synced = config.markSeen(item.studentId, {
			anchorAt: item.lastUpdatedAt || undefined,
			fileCountForUi: Number(item.newCount) || 0,
			applicationTargetId: item.applicationTargetId,
			cardId: item.id,
			status: item.status,
		})
		if (synced) onChanged?.()
	}, [
		item?.studentId,
		item?.lastUpdatedAt,
		item?.newCount,
		item?.applicationTargetId,
		item?.id,
		item?.status,
		onChanged,
		config,
	])

	const handleSee = useCallback(
		async file => {
			if (!item?.studentId || !file?.id) return
			try {
				const blob = await config.download(item.studentId, file)
				const url = URL.createObjectURL(blob)
				setPdfPreview({
					url,
					title: file.title || config.fallbackTitle,
				})
			} catch (e) {
				console.error(e)
			}
		},
		[item?.studentId, config],
	)

	const handleReload = useCallback(file => {
		console.warn('Re-upload not implemented yet for', file?.id)
	}, [])

	const handleClosePreview = useCallback(() => {
		setPdfPreview(prev => {
			if (prev?.url && prev.url.startsWith('blob:')) {
				URL.revokeObjectURL(prev.url)
			}
			return null
		})
	}, [])

	if (!item) return null

	const rawFiles = Array.isArray(item?.[config.listKey])
		? item[config.listKey]
		: []
	const files = rawFiles.map(f => ({
		...f,
		title: config.resolveTitle(f),
		updatedLabel: formatUpdated(f.uploadedAt),
	}))
	const count = Number(item.newCount) || files.length
	const noun = count === 1 ? config.nounSingular : config.nounPlural

	return (
		<>
			<PriorityDrawerShell
				item={item}
				items={items}
				index={index}
				onClose={onClose}
				onNavigate={onNavigate}
				navigationType="detailed"
				showFooter={items.length > 1}
			>
				<div className={s.body}>
					<div className={s.section}>
						<span className={s.eyebrow}>{config.eyebrow}</span>
						<h3 className={s.sectionTitle}>
							{count} {noun}
						</h3>
					</div>
					<div className={s.list}>
						{files.map(file => (
							<FileRow
								key={file.id}
								file={file}
								onSee={handleSee}
								onReload={handleReload}
							/>
						))}
					</div>
				</div>
			</PriorityDrawerShell>
			<PdfPreviewPortal
				open={Boolean(pdfPreview)}
				url={pdfPreview?.url}
				title={pdfPreview?.title}
				onClose={handleClosePreview}
			/>
		</>
	)
}

export default PriorityFilesDrawer
